import { useQuery } from "react-query";
import { fetchInternalFormConfig } from "../../../api";
import { FormConfig } from "../../../types/form";

interface Args {
  accountId: string;
  reportId: string;
}

interface ReturnType {
  formConfig: FormConfig | null | undefined;
  statusCode: number | null;
  isLoading: boolean;
}

interface ErrorWithResponse {
  response?: {
    status?: number;
  };
}

export const useFetchInternalFormConfig = ({ accountId, reportId }: Args): ReturnType => {
  const { data, isLoading, error } = useQuery<FormConfig, ErrorWithResponse>(
    ["internal-form", accountId, reportId],
    async () => await fetchInternalFormConfig(accountId, reportId),
    {
      refetchOnWindowFocus: true,
    }
  );

  const statusCode = defineStatusCode(isLoading, data, error);
  const formConfig = deriveFormConfig(isLoading, data, error);

  return { formConfig, statusCode, isLoading };
};

const deriveFormConfig = (
  isLoading: boolean,
  data: FormConfig | undefined,
  error: ErrorWithResponse | null | undefined
): FormConfig | null | undefined => {
  if (isLoading) return undefined;
  if (data !== undefined) return data;
  if (error) return null;
  return undefined;
};

const defineStatusCode = (
  isLoading: boolean,
  data: FormConfig | undefined,
  error?: ErrorWithResponse | null
): number | null => {
  if (isLoading) {
    return null;
  }

  if (data !== undefined) {
    return 200;
  }

  if (error?.response?.status) {
    return error.response.status;
  }

  return null;
};
